import { useMemo } from 'react';
import { useGetCustomVirtualBackgroundsQuery } from '../../services/branding/operations.enhanced';
import {
  BrandedVirtualBackgroundVariant,
  CustomVirtualBackgroundSource,
} from '../../services/graphQlTypes.generated';
import { VirtualBackgroundGroups } from '../../components/media/virtualBackground/constants';
import { useIsBrandedVirtualBackgroundEnabled } from './useIsBrandedVirtualBackgroundEnabled';

const BRANDED_INDEX_BY_VARIANT: Record<
  BrandedVirtualBackgroundVariant,
  number
> = {
  [BrandedVirtualBackgroundVariant.TopLeft]: 100,
  [BrandedVirtualBackgroundVariant.BottomLeft]: 101,
  [BrandedVirtualBackgroundVariant.Brick]: 102,
};

const VARIANT_METADATA: Record<
  BrandedVirtualBackgroundVariant,
  { name: string; title: string }
> = {
  [BrandedVirtualBackgroundVariant.TopLeft]: {
    name: 'Custom top left',
    title: 'virtualBackground.options.customTopLeft',
  },
  [BrandedVirtualBackgroundVariant.BottomLeft]: {
    name: 'Custom bottom left',
    title: 'virtualBackground.options.customBottomLeft',
  },
  [BrandedVirtualBackgroundVariant.Brick]: {
    name: 'Custom brick pattern',
    title: 'virtualBackground.options.customBrick',
  },
};

export const useBrandedVirtualBackgrounds = () => {
  const isEnabled = useIsBrandedVirtualBackgroundEnabled();
  const { data } = useGetCustomVirtualBackgroundsQuery(undefined, {
    skip: !isEnabled,
  });

  return useMemo(() => {
    if (!isEnabled) {
      return [];
    }

    const rows = data?.Tenant?.customVirtualBackgrounds ?? [];
    const branded = rows.filter(
      (row) =>
        row.source === CustomVirtualBackgroundSource.Branded &&
        row.brandedVariant !== null &&
        row.brandedVariant !== undefined,
    );

    return branded.map((row) => {
      const variant = row.brandedVariant as BrandedVirtualBackgroundVariant;
      return {
        index: BRANDED_INDEX_BY_VARIANT[variant],
        group: VirtualBackgroundGroups.Branded,
        name: VARIANT_METADATA[variant].name,
        filter: {
          type: 'backgroundReplacement' as const,
          backgroundImgUrl: row.url,
        },
        metadata: {
          title: VARIANT_METADATA[variant].title,
          thumbnail: row.url,
        },
      };
    });
  }, [data, isEnabled]);
};
